'use client';

import { GetStaticPaths, GetStaticProps } from 'next';
import { WithDefaultLayout } from '@/components/DefautLayout';
import { articles, categories } from '@/constants';
import { Article } from '@/interfaces/Article';
import SidebarArticles from '@/components/blog/SidebarArticles';
import ArticleCarousel from '@/components/blog/ArticleCarousel';
import ArticleDetails from '@/components/blog/ArticleDetails';
import Link from 'next/link';
import SearchFilterBar from '@/components/blog/SearchFilterBar';
import { useSearchParams, useRouter } from 'next/navigation';
import LatestArticle from '@/components/blog/LatestArticle';
import { Title } from '@/components/Title';

type Props = {
  article: Article | null;
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => ({
  paths: articles.map((a) => ({ params: { slug: a.slug } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<Props, { slug: string }> = async ({ params }) => {
  const slug = params?.slug;
  const article = slug ? articles.find((a) => a.slug === slug) || null : null;
  return { props: { article } };
};

const BlogDetailPage = ({ article }: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const categoryFromUrl = searchParams.get('category');
  
  if (!article) {
    return <div className="p-6 text-red-500">Artikel tidak ditemukan.</div>;
  }
  
  const selectedCategory = 
    categoryFromUrl && categories.includes(categoryFromUrl) 
      ? categoryFromUrl 
      : 'Semua';

  const sortedArticles = [...articles].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const filteredArticles =
    selectedCategory === 'Semua'
      ? sortedArticles
      : sortedArticles.filter((a) => a.category === selectedCategory);

  const latestArticle = filteredArticles.length > 0 ? filteredArticles[0] : null;
  const sidebarArticles = filteredArticles
    .filter((a) => a.slug !== article.slug)
    .slice(1, 5);

  const showDetail = selectedCategory === 'Semua';

  const onSelectCategory = (cat: string) => {
    router.push(`/blog/${article.slug}?category=${encodeURIComponent(cat)}`);
  };

  return (
    <>
    <Title title={article.title} description='' />
      <SearchFilterBar
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={onSelectCategory}
        articles={articles}
      />

      <div className="px-6 md:px-16 py-10 font-sans">
        <div className="flex items-center text-base text-gray-400 mb-4 gap-2">
          <Link href={`/blog?category=${encodeURIComponent(selectedCategory)}`}>
            <span>Blog</span>
          </Link>
          <span className="text-3xl font-light" style={{ transform: 'translateY(-3px)' }}>
            ›
          </span>
          <span className="font-medium text-gray-700">
            {showDetail ? article.category : selectedCategory}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {showDetail ? (
            <>
              <ArticleDetails article={article} />
              <SidebarArticles articles={sidebarArticles} />
            </>
          ) : (
            <>
             {latestArticle && (
              <div className="md:col-span-2">
                <LatestArticle article={latestArticle} />
              </div>
             )}
              <SidebarArticles articles={sidebarArticles} />
            </>
          )}
        </div>
      </div>
      <ArticleCarousel articles={filteredArticles} />
    </>
  );
};

BlogDetailPage.layout = WithDefaultLayout;
export default BlogDetailPage;